import { useCallback, useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import { toast } from "sonner";
import { X, Loader2, Minus, Plus } from "lucide-react";
import { adminApi } from "../../lib/adminApi";
import { assetUrl, formatApiErrorDetail } from "../../lib/api";

const ASPECT = 4 / 3;

/** Live preview of the cropped region at the actual card render aspect ratio. */
function LivePreview({ src, area }) {
  const [natural, setNatural] = useState(null);
  useEffect(() => {
    if (!src) {
      setNatural(null);
      return;
    }
    const img = new Image();
    img.onload = () => setNatural({ w: img.naturalWidth, h: img.naturalHeight });
    img.src = src;
  }, [src]);

  if (!src || !area || !natural) {
    return (
      <div className="w-full aspect-[4/3] rounded-lg bg-hbe-soft flex items-center justify-center text-xs text-hbe-ink/50">
        Position to preview
      </div>
    );
  }
  return (
    <div
      className="relative w-full aspect-[4/3] rounded-lg overflow-hidden bg-hbe-soft"
      data-testid="thumb-preview"
    >
      <img
        src={src}
        alt=""
        style={{
          position: "absolute",
          width: `${(natural.w / area.width) * 100}%`,
          height: `${(natural.h / area.height) * 100}%`,
          left: `${(-area.x / area.width) * 100}%`,
          top: `${(-area.y / area.height) * 100}%`,
          maxWidth: "none",
        }}
      />
    </div>
  );
}

/**
 * Modal that lets the admin pan/zoom the yacht thumbnail source to a 4:3
 * crop. Uses react-easy-crop; server does the actual cropping so the WebP
 * is deterministic across browsers. Persists `{x,y,width,height,zoom,aspect}`
 * on the yacht so re-opening the modal picks up where the admin left off.
 */
export default function ThumbnailCropModal({ open, yacht, onClose, onSaved }) {
  const [zoom, setZoom] = useState(1);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setZoom(yacht?.thumbnail_crop?.zoom || 1);
    setCrop({ x: 0, y: 0 });
    setCroppedAreaPixels(null);
    setError(null);
  }, [open, yacht?.id]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const onCropComplete = useCallback((_area, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  async function save() {
    if (!croppedAreaPixels) {
      setError("Position the image before saving.");
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const r = await adminApi.patch(
        `/admin/yachts/${yacht.id}/thumbnail/crop`,
        {
          crop: {
            x: Math.round(croppedAreaPixels.x),
            y: Math.round(croppedAreaPixels.y),
            width: Math.round(croppedAreaPixels.width),
            height: Math.round(croppedAreaPixels.height),
          },
          zoom,
          aspect: ASPECT,
        }
      );
      onSaved?.(r.data);
      toast.success("Thumbnail saved.");
      onClose();
    } catch (e) {
      setError(formatApiErrorDetail(e) || "Failed to save thumbnail.");
    } finally {
      setSaving(false);
    }
  }

  if (!open || !yacht) return null;
  const source = yacht.thumbnail_source_url
    ? assetUrl(yacht.thumbnail_source_url)
    : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
      data-testid="thumb-crop-modal"
    >
      <div
        className="w-full max-w-4xl max-h-[95vh] overflow-auto bg-white rounded-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 md:px-6 py-4 border-b border-hbe-line">
          <h2 className="font-serif text-lg md:text-xl text-hbe-primary">
            Position your thumbnail
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-hbe-ink/60 hover:text-hbe-primary min-w-[44px] min-h-[44px] inline-flex items-center justify-center rounded-full"
            aria-label="Close"
            data-testid="thumb-crop-close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-5 md:p-6 grid gap-6 md:grid-cols-[1fr_240px]">
          <div>
            <div className="relative w-full aspect-[4/3] bg-hbe-ink/95 rounded-xl overflow-hidden">
              {source && (
                <Cropper
                  image={source}
                  crop={crop}
                  zoom={zoom}
                  aspect={ASPECT}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                  showGrid
                  restrictPosition
                  minZoom={1}
                  maxZoom={5}
                />
              )}
            </div>
            <div className="mt-4 flex items-center gap-3">
              <button
                type="button"
                onClick={() => setZoom((z) => Math.max(1, +(z - 0.25).toFixed(2)))}
                className="text-hbe-primary min-w-[36px] min-h-[36px] rounded-full border border-hbe-line inline-flex items-center justify-center hover:bg-hbe-soft"
                aria-label="Zoom out"
                data-testid="thumb-zoom-out"
              >
                <Minus size={16} />
              </button>
              <input
                type="range"
                min={1}
                max={5}
                step={0.05}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="flex-1 accent-hbe-primary"
                aria-label="Zoom"
                data-testid="thumb-zoom-slider"
              />
              <button
                type="button"
                onClick={() => setZoom((z) => Math.min(5, +(z + 0.25).toFixed(2)))}
                className="text-hbe-primary min-w-[36px] min-h-[36px] rounded-full border border-hbe-line inline-flex items-center justify-center hover:bg-hbe-soft"
                aria-label="Zoom in"
                data-testid="thumb-zoom-in"
              >
                <Plus size={16} />
              </button>
            </div>
            <p className="mt-3 text-xs text-hbe-ink/55">
              Drag to pan · scroll or pinch to zoom · aspect locked to 4:3
            </p>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-hbe-accent mb-2">
              Preview
            </div>
            <LivePreview src={source} area={croppedAreaPixels} />
            <p className="mt-2 text-xs text-hbe-ink/55">
              How it appears on public yacht cards.
            </p>
          </div>
        </div>

        {error && (
          <div
            className="mx-5 md:mx-6 mb-4 rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2"
            data-testid="thumb-crop-error"
          >
            {error}
          </div>
        )}

        <div className="flex items-center justify-end gap-3 px-5 md:px-6 py-4 border-t border-hbe-line bg-hbe-soft/30">
          <button
            type="button"
            onClick={onClose}
            className="hbe-btn-ghost"
            data-testid="thumb-crop-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || !croppedAreaPixels}
            className="hbe-btn-primary"
            data-testid="thumb-crop-save"
          >
            {saving ? (
              <>
                <Loader2 size={14} className="mr-2 animate-spin" /> Saving…
              </>
            ) : (
              "Save thumbnail"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
